import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable, Injector, inject } from '@angular/core';
import { Observable, switchMap, catchError, throwError, from, filter, take, timeout } from 'rxjs';
import { isAssetRequest, isPublicRoute } from 'pi-api';
import { AuthService } from '../auth/auth.service';

@Injectable({
  providedIn: 'root',
})
export class RefreshTokenInterceptor implements HttpInterceptor {
  private inj = inject(Injector);

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Pomiń auth endpoints, login page, assety
    if (isPublicRoute(request.url) || this.isUserOnLoginPage() || isAssetRequest(request.url)) {
      return next.handle(request);
    }

    const authService = this.inj.get(AuthService);

    // PROAKTYWNY REFRESH: Sprawdź czy token wygasa wkrótce
    // authService.isTokenStillValid() - zwraca false gdy token wygaśnie w ciągu offsetSeconds
    if (!authService.isTokenStillValid()) {
      console.debug('RefreshInterceptor: token expiring soon, proactive refresh before request', request.url);
      return this.refreshAndProceed(request, next, authService);
    }

    // Token OK - kontynuuj (JwtModule doda Authorization header automatycznie)
    return next.handle(request);
  }

  private refreshAndProceed(
    request: HttpRequest<any>,
    next: HttpHandler,
    authService: AuthService
  ): Observable<HttpEvent<any>> {
    const refreshState = authService.refreshState$.value;

    // Jeśli refresh już w toku - czekaj na wynik
    if (refreshState.status === 'refreshing') {
      console.debug('RefreshInterceptor: refresh in progress, waiting', request.url);
      return this.waitForRefresh(request, next);
    }

    // Wywołaj refresh przez AuthService
    return from(authService.getRefreshPromise()).pipe(
      switchMap(() => {
        console.debug('RefreshInterceptor: proactive refresh complete', request.url);
        // JwtModule doda nowy token automatycznie (czyta z TokenService.getToken())
        return next.handle(request);
      }),
      catchError((error) => {
        // Refresh failed - propaguj error (ErrorHandler może podjąć decyzję)
        console.error('RefreshInterceptor: proactive refresh failed', request.url, error);
        return throwError(() => error);
      })
    );
  }

  private waitForRefresh(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const authService = this.inj.get(AuthService);

    return authService.refreshState$.pipe(
      filter((state) => state.status === 'success' || state.status === 'failed'),
      take(1),
      timeout(10000),
      switchMap((state) => {
        if (state.status === 'success') {
          // JwtModule doda nowy token automatycznie
          return next.handle(request);
        } else {
          return throwError(() => state.error || new Error('Token refresh failed'));
        }
      })
    );
  }

  private isUserOnLoginPage(): boolean {
    // Sprawdź czy użytkownik jest na stronie logowania
    const currentPath = window.location.pathname;
    const currentHash = window.location.hash;

    const isOnLoginPage =
      currentPath === '/login' ||
      currentHash === '#/login' ||
      currentPath.includes('login') ||
      currentHash.includes('login');

    if (isOnLoginPage) {
      console.debug('User detected on login page:', { currentPath, currentHash });
    }

    return isOnLoginPage;
  }

}
